import { Box, Divider, ListItemIcon, ListItemText, MenuItem, MenuList, Typography } from "@mui/material";
import { StyledTextField } from "../FilterPopover.tsx";
import CheckIcon from "@mui/icons-material/Check";
import React from "react";
import { IdNamePair } from "../../../../customLists/customListView/CustomListView.types.ts";
import { useSubMenuList } from "../FilterPopoverSubMenu.hooks.ts";
import { useTranslation } from "../../../../localization/useTranslation.ts";
import { DotLegalInformationTooltip, DotLegalPrettyScrollbar } from "@dotlegal/dotlegal-ui-components";
import FilterPopoverSubMenuEmptyState from "../FilterPopoverSubMenuEmptyState.tsx";
import { isNullOrWhitespace } from "../../../stringOperations.ts";

export interface SubMenuListProps {
    data: Array<IdNamePair>;
    onChange: (clickedOption: IdNamePair) => void;
    onLeftArrowClick?: () => void;
    chosenValues: Array<IdNamePair>;
    submenuInputRef: React.RefObject<HTMLInputElement>;
    onFilterChange?: (value: string) => void;
    filterValue?: string;
    separateListForSelectedValue?: boolean;
}

const subMenuItemSx = {
    fontSize: "14px",
    px: "12px",
    "& .MuiListItemIcon-root": {
        minWidth: 24,
    },
};

interface SubMenuListItemProps {
    id: string;
    name: string;
    tooltip?: string;
    // Only the rows in the navigable list carry an index; the chosen rows above the search field are not part of
    // keyboard navigation and have no highlight.
    index?: number;
    isSelected: boolean;
    isHighlighted: boolean;
    onHover?: (index: number) => void;
    onSelect: (id: string) => void;
    registerRef: (id: string, element: HTMLElement | null) => void;
}

// Memoized on primitive props and stable callbacks, so hovering re-renders the row that lost the highlight and the
// row that gained it, not every row in a list of several hundred.
const SubMenuListItem = React.memo(function SubMenuListItem(props: SubMenuListItemProps) {
    const { id, name, tooltip, index, isSelected, isHighlighted, onHover, onSelect, registerRef } = props;

    return (
        <MenuItem
            ref={(element) => registerRef(id, element)}
            id={index === undefined ? undefined : `submenu-option-${index}`}
            role="option"
            aria-selected={index === undefined ? undefined : isHighlighted}
            selected={isHighlighted}
            onMouseEnter={onHover && index !== undefined ? () => onHover(index) : undefined}
            onClick={() => onSelect(id)}
            sx={subMenuItemSx}
        >
            <ListItemIcon
                sx={{
                    minWidth: 24,
                    visibility: isSelected || isHighlighted ? "visible" : "hidden",
                    opacity: isHighlighted && !isSelected ? 0.4 : isSelected ? 1 : 0,
                }}
            >
                <CheckIcon fontSize="small" sx={{ fontSize: "16px" }} />
            </ListItemIcon>

            <ListItemText
                primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                            {name}
                        </Typography>
                        {tooltip && <DotLegalInformationTooltip small text={tooltip} />}
                    </Box>
                }
            />
        </MenuItem>
    );
});

function SubMenuList(props: SubMenuListProps) {
    const { chosenValues, submenuInputRef, separateListForSelectedValue } = props;

    const { translateString } = useTranslation();
    const {
        setSubmenuInputValue,
        submenuInputValue,
        handleKeyDown,
        submenuHighlightedIndex,
        filteredSubmenuItems,
        chosenIds,
        handleItemSelect,
        handleSubmenuItemHover,
        registerItemRef,
        setSubmenuHighlightedIndex,
    } = useSubMenuList(props);

    const getActiveDescendantId = () => {
        if (!open) return undefined;

        if (submenuHighlightedIndex >= 0) {
            return `submenu-option-${submenuHighlightedIndex}`;
        }

        return undefined;
    };

    return (
        <Box>
            {separateListForSelectedValue &&
                chosenValues.map((chosenOption) => (
                    <SubMenuListItem
                        key={chosenOption.id}
                        id={chosenOption.id}
                        name={chosenOption.name}
                        tooltip={chosenOption.tooltip}
                        isSelected
                        isHighlighted={false}
                        onSelect={handleItemSelect}
                        registerRef={registerItemRef}
                    />
                ))}

            <StyledTextField
                inputRef={submenuInputRef}
                fullWidth
                size="small"
                value={props.onFilterChange ? props.filterValue : submenuInputValue}
                onChange={
                    props.onFilterChange
                        ? (e) => {
                              props.onFilterChange!(e.target.value);
                              setSubmenuHighlightedIndex(0);
                          }
                        : (e) => {
                              setSubmenuInputValue(e.target.value);
                              setSubmenuHighlightedIndex(0);
                          }
                }
                onKeyDown={(e) => handleKeyDown(e, "submenu")}
                placeholder={`${translateString("filter")}...`}
                slotProps={{
                    input: {
                        inputProps: {
                            role: "combobox",
                            "aria-expanded": true,
                            "aria-haspopup": "listbox",
                            "aria-controls": "submenu-listbox",
                            "aria-activedescendant": getActiveDescendantId(),
                            "aria-autocomplete": "list",
                        },
                        disableUnderline: true,
                    },
                }}
                variant={"standard"}
                autoComplete="off"
            />

            <Divider />

            <DotLegalPrettyScrollbar>
                <MenuList id="submenu-listbox" role="listbox" aria-label="Items" sx={{ p: 0, maxHeight: 450 }}>
                    {filteredSubmenuItems.map((subItem, subIndex) => (
                        <SubMenuListItem
                            key={subItem.id}
                            id={subItem.id}
                            name={subItem.name}
                            tooltip={subItem.tooltip}
                            index={subIndex}
                            isSelected={chosenIds.has(subItem.id)}
                            isHighlighted={submenuHighlightedIndex === subIndex}
                            onHover={handleSubmenuItemHover}
                            onSelect={handleItemSelect}
                            registerRef={registerItemRef}
                        />
                    ))}
                    {filteredSubmenuItems.length === 0 && isNullOrWhitespace(props.filterValue) && isNullOrWhitespace(submenuInputValue) ? (
                        <FilterPopoverSubMenuEmptyState />
                    ) : filteredSubmenuItems.length === 0 && (!isNullOrWhitespace(props.filterValue) || !isNullOrWhitespace(submenuInputValue)) ? (
                        <FilterPopoverSubMenuEmptyState emptyText={translateString("noFilterResultsText")} />
                    ) : undefined}
                </MenuList>
            </DotLegalPrettyScrollbar>
        </Box>
    );
}

export default SubMenuList;
